import { useEffect, useState } from 'react'
import { api } from '@/lib/api'

interface FieldDef {
  id: string
  name: string
  fieldKey: string
  dataType: 'TEXT' | 'NUMBER' | 'DATE' | 'BOOLEAN' | 'PICKLIST' | 'MULTI_PICKLIST' | 'URL' | 'JSON'
  picklistOptions: string[]
  placeholder: string | null
}

interface FieldValue {
  id: string
  field: FieldDef
  valueText: string | null
  valueNumber: number | null
  valueDate: string | null
  valueBool: boolean | null
  valueJson: unknown
}

interface Props {
  clientStubId: string
}

export function CustomFieldsEditor({ clientStubId }: Props) {
  const [defs, setDefs] = useState<FieldDef[]>([])
  const [values, setValues] = useState<Record<string, FieldValue>>({})
  const [dirty, setDirty] = useState<Record<string, string>>({})

  async function load() {
    try {
      const [d, v] = await Promise.all([
        api<{ items: FieldDef[] }>('/v1/marketing/custom-fields/defs?scope=CLIENT'),
        api<{ items: FieldValue[] }>(`/v1/marketing/custom-fields/clients/${clientStubId}/values`),
      ])
      setDefs(d.items)
      const map: Record<string, FieldValue> = {}
      for (const val of v.items) map[val.field.fieldKey] = val
      setValues(map)
      setDirty({})
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientStubId])

  async function commit(fieldKey: string, dataType: FieldDef['dataType']) {
    const draftVal = dirty[fieldKey]
    if (draftVal === undefined) return
    const body: Record<string, unknown> = {}
    if (draftVal === '') {
      // borrar
      await api(`/v1/marketing/custom-fields/clients/${clientStubId}/values/${fieldKey}`, {
        method: 'DELETE',
      })
    } else {
      switch (dataType) {
        case 'NUMBER':
          body.valueNumber = Number(draftVal)
          break
        case 'BOOLEAN':
          body.valueBool = draftVal === 'true'
          break
        case 'DATE':
          body.valueDate = new Date(draftVal).toISOString()
          break
        default:
          body.valueText = draftVal
      }
      await api(`/v1/marketing/custom-fields/clients/${clientStubId}/values/${fieldKey}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      })
    }
    await load()
  }

  function currentText(def: FieldDef): string {
    const draft = dirty[def.fieldKey]
    if (draft !== undefined) return draft
    const v = values[def.fieldKey]
    if (!v) return ''
    if (def.dataType === 'NUMBER') return v.valueNumber?.toString() ?? ''
    if (def.dataType === 'BOOLEAN') return v.valueBool ? 'true' : 'false'
    if (def.dataType === 'DATE') return v.valueDate?.slice(0, 10) ?? ''
    return v.valueText ?? ''
  }

  if (defs.length === 0) return null

  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60 mb-1.5">
        campos personalizados
      </div>
      <ul className="space-y-2">
        {defs.map((def) => {
          const val = currentText(def)
          return (
            <li key={def.id}>
              <label className="block">
                <span className="text-[11px] text-ink-700">{def.name}</span>
                {def.dataType === 'PICKLIST' ? (
                  <select
                    value={val}
                    onChange={(e) => setDirty((d) => ({ ...d, [def.fieldKey]: e.target.value }))}
                    onBlur={() => void commit(def.fieldKey, def.dataType)}
                    className="mt-0.5 w-full border border-ink-900/15 rounded px-2 py-1 text-xs bg-white"
                  >
                    <option value="">—</option>
                    {def.picklistOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : def.dataType === 'BOOLEAN' ? (
                  <select
                    value={val || ''}
                    onChange={(e) => setDirty((d) => ({ ...d, [def.fieldKey]: e.target.value }))}
                    onBlur={() => void commit(def.fieldKey, def.dataType)}
                    className="mt-0.5 w-full border border-ink-900/15 rounded px-2 py-1 text-xs bg-white"
                  >
                    <option value="">—</option>
                    <option value="true">Sí</option>
                    <option value="false">No</option>
                  </select>
                ) : (
                  <input
                    type={
                      def.dataType === 'NUMBER'
                        ? 'number'
                        : def.dataType === 'DATE'
                          ? 'date'
                          : 'text'
                    }
                    value={val}
                    onChange={(e) => setDirty((d) => ({ ...d, [def.fieldKey]: e.target.value }))}
                    onBlur={() => void commit(def.fieldKey, def.dataType)}
                    placeholder={def.placeholder ?? ''}
                    className="mt-0.5 w-full border border-ink-900/15 rounded px-2 py-1 text-xs"
                  />
                )}
              </label>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
